import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    Typography,
    alpha,
} from '@mui/material';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import TableInfo from '../../components/TableInfo';
import http from '../../services/http';
import { formatAttendanceDuration } from '../../utils/formatAttendanceDuration';
import { getServiceGroups } from '../../redux/serviceGroup/serviceGroup.actions';
import { getUsers, selectPermissions } from '../../redux/user/user.actions';

const PRESETS = [
    { id: 'today', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: '30d', label: '30 dias' },
    { id: 'month', label: 'Mês atual' },
];

function SummaryCard({ label, value, sub, accent }) {
    const { theme } = useContext(ThemeContext);
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                height: '100%',
                borderRadius: 'var(--radius-md, 10px)',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography
                variant="h5"
                sx={{
                    mt: 0.75,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: accent ? 'primary.main' : 'text.primary',
                }}
            >
                {value}
            </Typography>
            {sub && (
                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    {sub}
                </Typography>
            )}
        </Paper>
    );
}

const TicketAttendanceReportPage = ({
    currentUser,
    permissions,
    users,
    serviceGroups,
    getUsers: loadUsers,
    getServiceGroups: loadServiceGroups,
}) => {
    const { theme } = useContext(ThemeContext);
    const history = useHistory();

    const [preset, setPreset] = useState('month');
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [report, setReport] = useState(null);
    const [serviceGroupId, setServiceGroupId] = useState('');
    const [userId, setUserId] = useState('');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [userDetail, setUserDetail] = useState(null);

    const canManage =
        Boolean(currentUser?.is_super_admin) ||
        (Array.isArray(permissions) && permissions.includes('#gerenciar-tickets'));

    useEffect(() => {
        if (canManage) {
            loadUsers();
            loadServiceGroups();
        }
    }, [canManage, loadUsers, loadServiceGroups]);

    const buildQuery = useCallback(() => {
        const params = new URLSearchParams();
        params.set('preset', preset);
        if (canManage && serviceGroupId) params.set('service_group_id', serviceGroupId);
        if (canManage && userId) params.set('user_id', userId);
        return params.toString();
    }, [preset, canManage, serviceGroupId, userId]);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = buildQuery();
            const res = await http.get(`/ticket-attendance/overview?${qs}`);
            setReport(res.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível carregar o relatório.');
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const openUserDetail = useCallback(async (row) => {
        if (!row?.user_id) return;
        setDrawerOpen(true);
        setDrawerLoading(true);
        setUserDetail(null);
        try {
            const params = new URLSearchParams();
            params.set('preset', preset);
            const res = await http.get(`/ticket-attendance/users/${row.user_id}?${params.toString()}`);
            setUserDetail(res.data);
        } catch (_) {
            setUserDetail(null);
        } finally {
            setDrawerLoading(false);
        }
    }, [preset]);

    const summary = report?.summary || {};
    const attendanceGridSx = useMemo(
        () => ({
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                outline: 'none',
            },
            '& .MuiDataGrid-cell .MuiTypography-caption': {
                color: `${theme.palette.text.secondary} !important`,
            },
            '& .MuiButton-text': {
                color: theme.palette.primary.main,
                fontWeight: 600,
            },
        }),
        [theme]
    );

    const usersRows = useMemo(
        () => (report?.users || []).map((u) => ({ ...u, id: u.user_id })),
        [report?.users]
    );
    const ticketsRows = useMemo(
        () => (report?.tickets || []).map((t) => ({ ...t, id: t.ticket_id })),
        [report?.tickets]
    );

    const userColumns = useMemo(
        () => [
            {
                field: 'user_name',
                headerName: 'Usuário',
                flex: 1.2,
                minWidth: 160,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                            {(params.row.user_name || '?').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ color: theme.palette.text.primary, lineHeight: 1.25 }}
                            >
                                {params.row.user_name || '—'}
                            </Typography>
                            <Typography
                                variant="caption"
                                noWrap
                                component="span"
                                sx={{
                                    display: 'block',
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.2,
                                }}
                            >
                                {params.row.user_email || ''}
                            </Typography>
                        </Box>
                    </Box>
                ),
            },
            {
                field: 'service_group_name',
                headerName: 'Departamento',
                flex: 0.9,
                minWidth: 120,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {params.row.service_group_name || '—'}
                    </Typography>
                ),
            },
            {
                field: 'total_seconds',
                headerName: 'Tempo no período',
                flex: 0.7,
                minWidth: 110,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                        {formatAttendanceDuration(params.row.total_seconds)}
                    </Typography>
                ),
            },
            {
                field: 'tickets_count',
                headerName: 'Tickets',
                width: 80,
                align: 'center',
                headerAlign: 'center',
            },
            {
                field: 'active_tickets',
                headerName: 'Ativos agora',
                width: 100,
                renderCell: (params) =>
                    params.row.active_tickets > 0 ? (
                        <Chip size="small" color="success" label={params.row.active_tickets} />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    ),
            },
            {
                field: 'actions',
                headerName: '',
                width: 100,
                sortable: false,
                renderCell: (params) => (
                    <Button
                        size="small"
                        variant="text"
                        onClick={(e) => {
                            e.stopPropagation();
                            openUserDetail(params.row);
                        }}
                    >
                        Detalhes
                    </Button>
                ),
            },
        ],
        [openUserDetail, theme.palette.text.primary, theme.palette.text.secondary]
    );

    const ticketColumns = useMemo(
        () => [
            {
                field: 'ticket_id',
                headerName: 'Ticket',
                width: 72,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {params.row.ticket_number}
                    </Typography>
                ),
            },
            {
                field: 'ticket_title',
                headerName: 'Assunto',
                flex: 1.4,
                minWidth: 180,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.primary" noWrap sx={{ fontWeight: 500 }}>
                        {params.row.ticket_title || '—'}
                    </Typography>
                ),
            },
            {
                field: 'assigned_to_name',
                headerName: 'Responsável',
                flex: 0.9,
                minWidth: 120,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {params.row.assigned_to_name || '—'}
                    </Typography>
                ),
            },
            {
                field: 'total_seconds',
                headerName: 'Tempo',
                flex: 0.6,
                minWidth: 100,
                renderCell: (params) => (
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        {params.row.has_active && (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                        )}
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            color="text.primary"
                            sx={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                            {formatAttendanceDuration(params.row.total_seconds)}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'users_count',
                headerName: 'Pessoas',
                width: 80,
                align: 'center',
                headerAlign: 'center',
            },
        ],
        []
    );

    const openTicketFromReport = useCallback(
        (ticketId) => {
            if (!ticketId) return;
            setDrawerOpen(false);
            history.push(`/ticket/${ticketId}`);
        },
        [history]
    );

    const detailTicketColumns = useMemo(
        () => [
            {
                field: 'ticket_id',
                headerName: 'ID',
                width: 64,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {params.row.ticket_number}
                    </Typography>
                ),
            },
            {
                field: 'ticket_title',
                headerName: 'Assunto',
                flex: 1,
                minWidth: 160,
                renderCell: (params) => (
                    <Typography
                        variant="body2"
                        noWrap
                        sx={{
                            fontWeight: 500,
                            color: 'primary.main',
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                            cursor: 'pointer',
                        }}
                    >
                        {params.row.ticket_title || '—'}
                    </Typography>
                ),
            },
            {
                field: 'total_seconds',
                headerName: 'Tempo',
                width: 100,
                renderCell: (params) => (
                    <Typography variant="body2" color="text.primary" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatAttendanceDuration(params.row.total_seconds)}
                    </Typography>
                ),
            },
            {
                field: 'has_active',
                headerName: 'Ativo',
                width: 72,
                renderCell: (params) =>
                    params.row.has_active ? (
                        <Chip size="small" color="success" label="Sim" />
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            —
                        </Typography>
                    ),
            },
        ],
        []
    );

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default',
                p: { xs: 1.5, md: 2.5 },
            }}
        >
            <Box sx={{ flexShrink: 0, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                    <TimerOutlinedIcon sx={{ color: 'primary.main', fontSize: 32, mt: 0.25 }} />
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                        <Typography variant="h5" fontWeight={700} color="text.primary">
                            Tempo em tickets
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                            Relação entre operadores, tickets e tempo de atendimento registrado.
                            {canManage
                                ? ' Você pode filtrar por departamento e usuário.'
                                : ' Você está vendo apenas o seu tempo de trabalho nos tickets.'}
                        </Typography>
                    </Box>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        mt: 2,
                        p: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        alignItems: 'center',
                        borderRadius: 'var(--radius-md, 10px)',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {PRESETS.map((p) => (
                            <Chip
                                key={p.id}
                                label={p.label}
                                clickable
                                color={preset === p.id ? 'primary' : 'default'}
                                variant={preset === p.id ? 'filled' : 'outlined'}
                                onClick={() => setPreset(p.id)}
                            />
                        ))}
                    </Box>

                    {canManage && (
                        <>
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Departamento</InputLabel>
                                <Select
                                    label="Departamento"
                                    value={serviceGroupId}
                                    onChange={(e) => {
                                        setServiceGroupId(e.target.value);
                                        setUserId('');
                                    }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {(serviceGroups || []).map((g) => (
                                        <MenuItem key={g.id} value={String(g.id)}>
                                            {g.description || g.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                                <InputLabel>Usuário</InputLabel>
                                <Select
                                    label="Usuário"
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {(users || [])
                                        .filter((u) => !serviceGroupId || String(u.service_group_id) === String(serviceGroupId))
                                        .map((u) => (
                                            <MenuItem key={u.id} value={String(u.id)}>
                                                {u.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </>
                    )}

                    <Button variant="outlined" size="small" onClick={fetchReport} disabled={loading}>
                        Atualizar
                    </Button>
                </Paper>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {loading && !report ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Grid container spacing={1.5} sx={{ flexShrink: 0, mb: 2 }}>
                        <Grid item xs={6} sm={3}>
                            <SummaryCard
                                label="Tempo total"
                                value={formatAttendanceDuration(summary.total_seconds)}
                                accent
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <SummaryCard
                                label="Tickets com atividade"
                                value={summary.tickets_count ?? 0}
                                sub={`${summary.sessions_count ?? 0} segmentos`}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <SummaryCard
                                label="Operadores"
                                value={summary.users_count ?? 0}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <SummaryCard
                                label="Ativos agora"
                                value={summary.active_tickets_now ?? 0}
                                sub="tickets com cronômetro ligado"
                            />
                        </Grid>
                    </Grid>

                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: 'var(--radius-md, 10px)',
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden',
                        }}
                    >
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            sx={{
                                px: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                                minHeight: 44,
                            }}
                        >
                            <Tab icon={<PersonOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Por usuário" />
                            <Tab icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Por ticket" />
                        </Tabs>

                        <Box sx={{ flex: 1, minHeight: 280, p: 1, bgcolor: 'background.paper' }}>
                            {tab === 0 && (
                                <TableInfo
                                    rows={usersRows}
                                    columns={userColumns}
                                    fullHeight
                                    density="compact"
                                    useThemeRows
                                    isBorder={false}
                                    onRowClick={(params) => openUserDetail(params.row)}
                                    sx={attendanceGridSx}
                                />
                            )}
                            {tab === 1 && (
                                <TableInfo
                                    rows={ticketsRows}
                                    columns={ticketColumns}
                                    fullHeight
                                    density="compact"
                                    useThemeRows
                                    isBorder={false}
                                    onRowClick={(params) => openTicketFromReport(params.row.ticket_id)}
                                    sx={attendanceGridSx}
                                />
                            )}
                        </Box>
                    </Paper>
                </Box>
            )}

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 420 },
                        maxWidth: '100vw',
                        p: 2,
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        borderTopLeftRadius: 'var(--radius-lg, 14px)',
                        borderBottomLeftRadius: 'var(--radius-lg, 14px)',
                    },
                }}
            >
                {drawerLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : userDetail?.user ? (
                    <>
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                            {userDetail.user.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {userDetail.user.email}
                            {userDetail.user.service_group_name
                                ? ` · ${userDetail.user.service_group_name}`
                                : ''}
                        </Typography>
                        <Box
                            sx={{
                                my: 2,
                                p: 1.5,
                                borderRadius: 'var(--radius-md, 10px)',
                                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                            }}
                        >
                            <Typography variant="body2">
                                Tempo no período:{' '}
                                <strong>{formatAttendanceDuration(userDetail.summary?.total_seconds)}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {userDetail.summary?.tickets_count ?? 0} tickets ·{' '}
                                {userDetail.summary?.active_tickets_now ?? 0} ativo(s) agora
                            </Typography>
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            Tickets trabalhados
                        </Typography>
                        <Box sx={{ height: 360 }}>
                            <TableInfo
                                rows={(userDetail.tickets || []).map((t) => ({ ...t, id: t.ticket_id }))}
                                columns={detailTicketColumns}
                                density="compact"
                                autoHeight
                                useThemeRows
                                isBorder={false}
                                onRowClick={(params) => openTicketFromReport(params.row.ticket_id)}
                                sx={attendanceGridSx}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography color="text.secondary">Não foi possível carregar o detalhe.</Typography>
                )}
            </Drawer>
        </Box>
    );
};

const mapStateToProps = (state) => ({
    currentUser: state.user.currentUser,
    permissions: selectPermissions(state),
    users: state.user.users,
    serviceGroups: state.serviceGroups?.serviceGroups || [],
});

const mapDispatchToProps = (dispatch) => ({
    getUsers: () => dispatch(getUsers()),
    getServiceGroups: () => dispatch(getServiceGroups()),
});

export default connect(mapStateToProps, mapDispatchToProps)(TicketAttendanceReportPage);
